import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, BookOpen, ArrowRight, Plus, FileText, Shield,
  Waves, Briefcase, ClipboardList, Clock, Users, Dumbbell,
  GraduationCap, FolderOpen,
  ChevronRight, AlertCircle, Loader2
} from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import PageLayout from '../../components/layout/PageLayout';
import {
  getLibraryItems,
  getLibraryStats,
  ITEM_TYPES,
  ITEM_STATUSES,
  SPORT_LABELS,
} from '../../services/libraryService';

// ─── Section Card ────────────────────────────────────────────────────
const SectionCard = ({ icon: Icon, title, description, cta, onClick, accent }) => (
  <button
    onClick={onClick || undefined}
    className="group bg-white rounded-2xl border text-left w-full flex flex-col h-full
               hover:shadow-md transition-all duration-200 overflow-hidden"
    style={{ borderColor: VUKZ.border }}
  >
    {accent && <div className="h-1 w-full" style={{ background: accent }} />}
    <div className="p-6 flex flex-col flex-1">
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-5
                   group-hover:scale-105 transition-transform duration-200"
        style={{ backgroundColor: VUKZ.primaryLight }}
      >
        <Icon className="w-7 h-7" style={{ color: VUKZ.primary }} />
      </div>
      <h3
        className="text-base font-bold mb-2 leading-snug"
        style={{ color: VUKZ.text }}
      >
        {title}
      </h3>
      <p
        className="text-sm leading-relaxed mb-4 flex-1"
        style={{ color: VUKZ.textLight }}
      >
        {description}
      </p>
      <div className="flex items-center gap-1.5 text-sm font-semibold mt-auto"
           style={{ color: VUKZ.primary }}>
        {cta}
        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  </button>
);

// ─── Stat Block ──────────────────────────────────────────────────────
const StatBlock = ({ label, value, icon: Icon }) => (
  <div className="flex items-center gap-3 bg-white rounded-xl border px-5 py-4"
       style={{ borderColor: VUKZ.border }}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
         style={{ backgroundColor: VUKZ.primaryLight }}>
      <Icon className="w-5 h-5" style={{ color: VUKZ.primary }} />
    </div>
    <div>
      <div className="text-xl font-black tabular-nums" style={{ color: VUKZ.primary }}>
        {value}
      </div>
      <div className="text-xs" style={{ color: VUKZ.textLight }}>{label}</div>
    </div>
  </div>
);

// ─── Item Row ────────────────────────────────────────────────────────
const ItemRow = ({ item, onClick }) => {
  const typeMeta = ITEM_TYPES[item.item_type?.toLowerCase()] || ITEM_TYPES.protocol;
  const statusMeta = ITEM_STATUSES[item.status?.toLowerCase()] || ITEM_STATUSES.draft;

  return (
    <button
      onClick={() => onClick(item)}
      className="group w-full flex items-center gap-4 px-5 py-4 border-b text-left
                 hover:bg-gray-50 transition-colors"
      style={{ borderColor: VUKZ.border }}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
           style={{ backgroundColor: VUKZ.primaryLight }}>
        <FileText className="w-5 h-5" style={{ color: VUKZ.textLight }} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold truncate group-hover:underline"
            style={{ color: VUKZ.text }}>
          {item.title}
        </h4>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs" style={{ color: VUKZ.textLight }}>
            {typeMeta.label}
          </span>
          {(item.sport || item.sport_type) && (
            <span className="text-xs font-medium px-2 py-0.5 rounded"
                  style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
              {SPORT_LABELS[item.sport || item.sport_type] || item.sport_type}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusMeta.dotColor }} />
        <span className="text-xs" style={{ color: VUKZ.textLight }}>
          {statusMeta.label}
        </span>
      </div>
      <ArrowRight className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: VUKZ.primary }} />
    </button>
  );
};

// ─── Profile Section (placeholder for future roles) ──────────────────
const ProfileSection = ({ icon: Icon, title, topics, disabled }) => (
  <div
    className={"bg-white rounded-2xl border p-6 " + (disabled ? "opacity-50" : "")}
    style={{ borderColor: VUKZ.border }}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
           style={{ backgroundColor: VUKZ.primaryLight }}>
        <Icon className="w-5 h-5" style={{ color: VUKZ.primary }} />
      </div>
      <div>
        <h3 className="text-base font-bold" style={{ color: VUKZ.text }}>{title}</h3>
        {disabled && (
          <span className="text-xs" style={{ color: VUKZ.textLight }}>Em breve</span>
        )}
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      {topics.map((topic) => (
        <span
          key={topic}
          className="text-xs px-3 py-1.5 rounded-lg border"
          style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
        >
          {topic}
        </span>
      ))}
    </div>
  </div>
);

// ─── Swimming Topics ─────────────────────────────────────────────────
const SWIMMING_TOPICS = [
  'Crawl', 'Costas', 'Peito', 'Borboleta',
  'Saida', 'Virada', 'Submerso',
  'Ritmo de prova', 'Contagem de bracadas',
  'Eficiencia tecnica', 'Analise de video',
  'Preparacao fisica', 'Categorias de base', 'Alto rendimento',
];

// =====================================================================
// MAIN PAGE
// =====================================================================
const LibraryHubPage = () => {
  const navigate = useNavigate();

  const [items, setItems]     = useState([]);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // ── Fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [allItems, allStats] = await Promise.all([
          getLibraryItems(),
          getLibraryStats(),
        ]);
        if (!cancelled) {
          setItems(allItems);
          setStats(allStats);
        }
      } catch (err) {
        console.error('[LibraryHub] load error:', err);
        if (!cancelled) setError('Nao foi possivel carregar a biblioteca.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────
  const recentItems = useMemo(
    () => [...items]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 6),
    [items]
  );

  const swimmingCount = useMemo(
    () => items.filter((i) => (i.sport || i.sport_type || i.modality) === 'swimming').length,
    [items]
  );

  const methodologyCount = useMemo(
    () => items.filter((i) => (i.item_type || '').toLowerCase() === 'methodology').length,
    [items]
  );

  // ── Filter options ─────────────────────────────────────────────────
  const FILTERS = [
    { key: 'all',        label: 'Todos' },
    { key: 'swimming',   label: 'Natacao' },
    { key: 'methodology',label: 'Metodologias' },
    { key: 'approved',   label: 'Aprovados' },
  ];

  const filteredRecentItems = useMemo(() => {
    if (activeFilter === 'all') return recentItems;
    if (activeFilter === 'swimming') return recentItems.filter((i) => (i.sport || i.sport_type) === 'swimming');
    if (activeFilter === 'methodology') return recentItems.filter((i) => (i.item_type || '').toLowerCase() === 'methodology');
    if (activeFilter === 'approved') return recentItems.filter((i) => i.status?.toLowerCase() === 'approved');
    return recentItems;
  }, [recentItems, activeFilter]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate("/library/search?q=" + encodeURIComponent(searchTerm.trim()));
    }
  };

  const handleItemClick = (item) => {
    navigate("/library/item/" + item.item_id);
  };

  // ── Loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageLayout header={{ title: 'Biblioteca', subtitle: 'Carregando...' }}>
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: VUKZ.textLight }} />
          <p className="text-sm" style={{ color: VUKZ.textLight }}>Carregando biblioteca...</p>
        </div>
      </PageLayout>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <PageLayout header={{ title: 'Biblioteca' }}>
        <div className="flex flex-col items-center justify-center py-24">
          <AlertCircle className="w-10 h-10 mb-4" style={{ color: VUKZ.error }} />
          <p className="text-sm font-medium mb-4" style={{ color: VUKZ.text }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 rounded-xl text-sm font-bold"
            style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
          >
            Tentar novamente
          </button>
        </div>
      </PageLayout>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────
  if (items.length === 0 && !loading) {
    return (
      <PageLayout
        header={{
          title: 'Biblioteca',
          subtitle: 'Central de conhecimento tecnico, metodologico e institucional do clube.',
          actions: (
            <Link
              to="/library/create"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold hover:opacity-90"
              style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
            >
              <Plus className="w-4 h-4" /> Novo Item
            </Link>
          ),
        }}
      >
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FolderOpen className="w-16 h-16 mb-6" style={{ color: VUKZ.border }} />
          <h3 className="text-xl font-bold mb-2" style={{ color: VUKZ.text }}>
            Biblioteca vazia
          </h3>
          <p className="text-sm mb-8 max-w-md" style={{ color: VUKZ.textLight }}>
            Comece adicionando protocolos, metodologias, drills ou documentos do clube.
          </p>
          <Link
            to="/library/create"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold hover:opacity-90"
            style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
          >
            <Plus className="w-4 h-4" /> Criar primeiro item
          </Link>
        </div>
      </PageLayout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <PageLayout
      header={{
        title: 'Biblioteca',
        subtitle: 'Central de conhecimento tecnico, metodologico e institucional do clube.',
        actions: (
          <Link
            to="/library/create"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold hover:opacity-90"
            style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
          >
            <Plus className="w-4 h-4" /> Novo Item
          </Link>
        ),
      }}
    >
      <div className="space-y-10">

        {/* SEARCH BAR */}
        <section>
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: VUKZ.textLight }} />
              <input
                type="text"
                placeholder="Buscar por titulo, palavra-chave ou tema..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border text-sm font-medium
                           placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                style={{ borderColor: VUKZ.border, color: VUKZ.text }}
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3.5 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity flex-shrink-0"
              style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
            >
              Pesquisar
            </button>
          </form>

          {/* Quick filter chips */}
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-xs font-semibold py-2 mr-1" style={{ color: VUKZ.textLight }}>
              Filtros:
            </span>
            {['Modalidade', 'Perfil', 'Tipo de conteudo', 'Status'].map((f) => (
              <button
                key={f}
                onClick={() => navigate("/library/search?filter=" + encodeURIComponent(f))}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border hover:shadow-sm transition-shadow"
                style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
              >
                {f}
              </button>
            ))}
          </div>
        </section>

        {/* STATS */}
        {stats && (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatBlock label="Total de itens"  value={stats.total}                   icon={BookOpen} />
            <StatBlock label="Aprovados"       value={stats.byStatus?.approved || 0} icon={Shield} />
            <StatBlock label="Natacao"         value={swimmingCount}                  icon={Waves} />
            <StatBlock label="Metodologias"    value={methodologyCount}               icon={ClipboardList} />
          </section>
        )}

        {/* MAIN CARDS — 6 sections */}
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

            <SectionCard
              icon={FolderOpen}
              title="Biblioteca Geral"
              description="Conteudos institucionais e transversais: ciencia do esporte, governanca, boas praticas, regulamentos e referencias tecnicas."
              cta="Explorar conteudos gerais"
              onClick={() => navigate('/library/search')}
              accent={VUKZ.primary}
            />

            <SectionCard
              icon={Waves}
              title="Natacao"
              description="Tecnica, estilos, saida, virada, submerso, ritmo de prova, analise de video, preparacao fisica e categorias de base."
              cta="Abrir biblioteca de natacao"
              onClick={() => navigate('/library/search?sport=swimming')}
              accent="#0369a1"
            />

            <SectionCard
              icon={Briefcase}
              title="Gestor"
              description="Metodologia do clube, planejamento estrategico, diretrizes por categoria, indicadores de performance e relatorios executivos."
              cta="Ver conteudos do gestor"
              onClick={() => navigate('/library/search?profile=gestor')}
              accent={VUKZ.primary}
            />

            <SectionCard
              icon={BookOpen}
              title="Metodologias"
              description="Modelos de periodizacao, referencia cientifica e templates prontos para importar no planejamento do clube."
              cta="Ver metodologias"
              onClick={() => navigate('/library/methodologies')}
              accent={VUKZ.primary}
            />

            <SectionCard
              icon={Shield}
              title="Documentos Aprovados"
              description="Conteudos revisados e aprovados pela gestao. Documentos oficiais, politicas e materiais validados do clube."
              cta="Ver documentos oficiais"
              onClick={() => navigate('/library/search?status=approved')}
              accent={VUKZ.success}
            />

            <SectionCard
              icon={Clock}
              title="Conteudos Recentes"
              description="Ultimos itens adicionados ou atualizados na biblioteca. Acompanhe as novidades do acervo."
              cta="Ver atualizacoes"
              onClick={() => navigate('/library/search?sort=recent')}
              accent={VUKZ.primary}
            />
          </div>
        </section>

        {/* NATACAO — Highlighted Section */}
        <section
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
        >
          <div className="p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-2">
              <Waves className="w-6 h-6" style={{ color: '#0369a1' }} />
              <h2 className="text-lg font-bold" style={{ color: VUKZ.text }}>
                Biblioteca de Natacao
              </h2>
            </div>
            <p className="text-sm mb-6" style={{ color: VUKZ.textLight }}>
              Conteudos tecnicos e metodologicos organizados por tema. Selecione um topico para explorar.
            </p>
            <div className="flex flex-wrap gap-2">
              {SWIMMING_TOPICS.map((topic) => (
                <button
                  key={topic}
                  onClick={() => navigate("/library/search?q=" + encodeURIComponent(topic) + "&sport=swimming")}
                  className="text-sm font-medium px-4 py-2 rounded-xl border
                             hover:shadow-sm hover:border-gray-300 transition-all"
                  style={{ borderColor: VUKZ.border, color: VUKZ.text }}
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* PROFILE SECTIONS (placeholders) */}
        <section>
          <h2 className="text-lg font-bold mb-5" style={{ color: VUKZ.text }}>
            Conteudos por perfil
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ProfileSection
              icon={Briefcase}
              title="Gestor"
              topics={[
                'Metodologia do clube', 'Planejamento estrategico',
                'Diretrizes por categoria', 'Indicadores', 'Relatorios',
                'Tomada de decisao', 'Materiais para reuniao',
              ]}
            />
            <ProfileSection
              icon={Users}
              title="Treinador"
              disabled
              topics={[
                'Tecnica de natacao', 'Sessoes de treino',
                'Correcoes tecnicas', 'Analise de prova',
                'Progressoes pedagogicas',
              ]}
            />
            <ProfileSection
              icon={Dumbbell}
              title="Preparador Fisico"
              disabled
              topics={[
                'Carga', 'Forca', 'Mobilidade',
                'Prevencao', 'Recovery', 'Avaliacao fisica',
              ]}
            />
            <ProfileSection
              icon={GraduationCap}
              title="Atleta"
              disabled
              topics={[
                'Orientacao', 'Rotina', 'Recuperacao',
                'Educacao esportiva', 'Metas', 'Videos',
              ]}
            />
          </div>
        </section>

        {/* RECENT ITEMS */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold" style={{ color: VUKZ.text }}>
              Adicionados recentemente
            </h2>
            <Link
              to="/library/search?sort=recent"
              className="text-sm font-semibold flex items-center gap-1 hover:underline"
              style={{ color: VUKZ.primary }}
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="flex gap-2 mb-4">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all"
                style={{
                  backgroundColor: activeFilter === f.key ? VUKZ.primary : '#fff',
                  color: activeFilter === f.key ? '#fff' : VUKZ.text,
                  borderColor: activeFilter === f.key ? VUKZ.primary : VUKZ.border,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div
            className="bg-white rounded-2xl border overflow-hidden"
            style={{ borderColor: VUKZ.border }}
          >
            {filteredRecentItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="w-10 h-10 mb-3" style={{ color: VUKZ.border }} />
                <p className="text-sm" style={{ color: VUKZ.textLight }}>
                  Nenhum item encontrado para este filtro.
                </p>
              </div>
            ) : (
              filteredRecentItems.map((item) => (
                <ItemRow key={item.item_id} item={item} onClick={handleItemClick} />
              ))
            )}
          </div>
        </section>

        {/* METHODOLOGY CTA */}
        <section
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
        >
          <div className="p-6 lg:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5" style={{ color: VUKZ.primary }} />
                <span className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: VUKZ.textLight }}>
                  Metodologias de Treino
                </span>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: VUKZ.text }}>
                Modelos de periodizacao e referencia cientifica
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: VUKZ.textLight }}>
                Explore modelos como Polarizado, Piramidal, USRPT e Limiar.
                Cada metodologia inclui templates prontos para importar no planejamento.
              </p>
            </div>
            <Link
              to="/library/methodologies"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold hover:opacity-90 flex-shrink-0"
              style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
            >
              Explorar Metodologias
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

      </div>
    </PageLayout>
  );
};

export default LibraryHubPage;
